import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useStore } from '../context/StoreContext';
import { shiftsAPI, extractErrorMessage } from '../api';
import { api } from '../core/api/httpClient';
import { useToast } from '../components/Toast';

const ZReport = () => {
  const { selectedStore } = useStore();
  const showToast = useToast();
  const [expandedShiftId, setExpandedShiftId] = useState(null);
  const [reportData, setReportData] = useState({});

  const { data: shifts = [], isLoading, error } = useQuery({
    queryKey: ['shifts', selectedStore?.id],
    queryFn: () => shiftsAPI.list(selectedStore.id).then(r => {
      const d = r.data;
      return Array.isArray(d) ? d : d.items || [];
    }),
    enabled: !!selectedStore?.id,
  });

  const generateReport = useMutation({
    mutationFn: (shiftId) => api.post(`/shifts/${shiftId}/z-report`).then(r => r.data),
    onSuccess: (data, shiftId) => {
      setReportData(prev => ({ ...prev, [shiftId]: data }));
      setExpandedShiftId(shiftId);
    },
    onError: (err) => {
      showToast(extractErrorMessage(err, 'Failed to generate Z-Report'), 'error');
    },
  });

  const closedShifts = shifts.filter(s => s.status === 'closed');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedStore?.currency || 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (isLoading) {
    return <div className="loading"><div className="spinner"></div><span>Loading shifts...</span></div>;
  }

  if (error) {
    return <div className="alert alert-danger">{extractErrorMessage(error, 'Failed to load shifts')}</div>;
  }

  return (
    <div className="page-modern">
      <div className="page-header">
        <div>
          <h1 className="page-title">Z-Reports</h1>
          <p className="page-subtitle">End-of-day shift summaries and cash reconciliation</p>
        </div>
      </div>

      {closedShifts.length === 0 ? (
        <div className="page-empty-state">
          <div className="page-empty-title">No closed shifts</div>
          <p className="page-empty-text">Z-Reports are generated from closed shifts.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {closedShifts.map(shift => {
            const report = reportData[shift.id];
            const isExpanded = expandedShiftId === shift.id;
            const isGenerating = generateReport.isPending && generateReport.variables === shift.id;

            return (
              <div key={shift.id} className="card">
                <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h3 className="card-title" style={{ marginBottom: 4 }}>
                      Shift #{shift.id}
                    </h3>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                      {formatDate(shift.opened_at)} — {formatDate(shift.closed_at)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {report && (
                      <button className="btn btn-sm btn-secondary" onClick={() => window.print()}>
                        Print
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => generateReport.mutate(shift.id)}
                      disabled={isGenerating}
                    >
                      {isGenerating ? 'Generating...' : 'Generate Z-Report'}
                    </button>
                  </div>
                </div>

                {isExpanded && report && (
                  <div className="card-body">
                    <div className="page-stats-row" style={{ marginBottom: 24 }}>
                      <div className="page-stat-card">
                        <div className="page-stat-content">
                          <div className="page-stat-label">Total Sales</div>
                          <div className="page-stat-value">{formatCurrency(report.total_sales)}</div>
                        </div>
                      </div>
                      <div className="page-stat-card">
                        <div className="page-stat-content">
                          <div className="page-stat-label">Returns</div>
                          <div className="page-stat-value">{formatCurrency(report.total_returns)}</div>
                        </div>
                      </div>
                      <div className="page-stat-card">
                        <div className="page-stat-content">
                          <div className="page-stat-label">Opening Float</div>
                          <div className="page-stat-value">{formatCurrency(report.opening_float)}</div>
                        </div>
                      </div>
                      <div className="page-stat-card">
                        <div className="page-stat-content">
                          <div className="page-stat-label">Net Cash</div>
                          <div className="page-stat-value" style={{ color: 'var(--success)' }}>
                            {formatCurrency(report.net_cash)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {report.by_payment_method && report.by_payment_method.length > 0 && (
                      <div className="card" style={{ marginBottom: 16 }}>
                        <div className="card-header">
                          <h3 className="card-title">Sales by Payment Method</h3>
                        </div>
                        <div className="card-body" style={{ padding: 0 }}>
                          <div className="table-container">
                            <table>
                              <thead>
                                <tr>
                                  <th>Payment Method</th>
                                  <th style={{ textAlign: 'center' }}>Transactions</th>
                                  <th style={{ textAlign: 'right' }}>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {report.by_payment_method.map((row, i) => (
                                  <tr key={i}>
                                    <td className="font-medium" style={{ textTransform: 'capitalize' }}>
                                      {(row.payment_method || '').replace('_', ' ')}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>{row.count || 0}</td>
                                    <td style={{ textAlign: 'right' }}>{formatCurrency(row.total_amount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <div style={{ padding: 16, background: 'var(--bg)', borderRadius: 8 }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 4 }}>Opening Float</div>
                        <div style={{ fontWeight: 600 }}>{formatCurrency(report.opening_float)}</div>
                      </div>
                      <div style={{ padding: 16, background: 'var(--bg)', borderRadius: 8 }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 4 }}>Closing Float</div>
                        <div style={{ fontWeight: 600 }}>{formatCurrency(report.closing_float)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ZReport;
